import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ShoppingCart, Truck, ShieldCheck, Phone, Star, Check, Minus, Plus, MessageCircle, Send, Info, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/products/ProductCard';
import { useCart } from '../context/CartContext';
import { reviews, companyInfo, standardEquipment, warrantyByFrame, warrantyDisclaimer, polycarbonateOptions, polycarbonateInfo, additionalOptions } from '../data/mock';
import { Helmet } from 'react-helmet-async';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Состояние для опций
  const [selectedPolycarbonate, setSelectedPolycarbonate] = useState('standard');
  const [showPolyInfo, setShowPolyInfo] = useState(false);
  
  // Дополнительные опции
  const [fortochkaCount, setFortochkaCount] = useState(0);
  const [termoprivodCount, setTermoprivodCount] = useState(0);
  const [termoshayby, setTermoshayby] = useState(false);
  const [fundament, setFundament] = useState(false);
  const [montazh, setMontazh] = useState(false);
  const [gryadki, setGryadki] = useState(false);

  useEffect(() => {
    fetch('/api/products')
      .then(res => res.json())
      .then(data => {
        setProducts(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading products:', err);
        setLoading(false);
      });
  }, []);

  const product = products.find(p => p.slug === slug);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Товар не найден</h1>
          <Link to="/catalog">
            <Button>Вернуться в каталог</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Получаем размер теплицы для расчёта цен
  const sizeKey = `${product.size.width}x${product.size.length}`;

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  // Получаем выбранный поликарбонат
  const currentPolycarbonate = polycarbonateOptions.find(p => p.id === selectedPolycarbonate);
  
  // Определяем, является ли теплица типом "Домиком"
  const isDomikom = product.category === 'domikom' || product.name?.toLowerCase().includes('домиком');
  
  // Цена поликарбоната: для Домиком используем priceBySizeDomikom, для остальных - priceBySize
  const polycarbonatePrice = isDomikom 
    ? (currentPolycarbonate?.priceBySizeDomikom?.[sizeKey] || currentPolycarbonate?.priceAdd || 0)
    : (currentPolycarbonate?.priceBySize?.[sizeKey] || currentPolycarbonate?.priceAdd || 0);

  // Рассчитываем цены дополнительных опций
  const termoshaybyPrice = termoshayby ? (additionalOptions.termoshayby.priceBySize[sizeKey] || 0) : 0;
  const fundamentPrice = fundament ? (additionalOptions.fundament.priceBySize[sizeKey] || 0) : 0;
  // Для Домиком используем специальные цены на монтаж
  const montazhPrice = montazh ? (
    isDomikom 
      ? (additionalOptions.montazh.priceBySizeDomikom?.[sizeKey] || additionalOptions.montazh.priceBySize[sizeKey] || 0)
      : (additionalOptions.montazh.priceBySize[sizeKey] || 0)
  ) : 0;
  const fortochkaPrice = fortochkaCount * additionalOptions.fortochka.price;
  const termoprivodPrice = termoprivodCount * additionalOptions.termoprivod.price;
  const gryadkiPrice = gryadki ? (additionalOptions.gryadki.priceBySize[sizeKey] || 0) : 0;

  // Итоговая цена
  const totalPrice = product.price + 
    polycarbonatePrice + 
    fortochkaPrice + 
    termoprivodPrice + 
    termoshaybyPrice + 
    fundamentPrice + 
    montazhPrice +
    gryadkiPrice;

  // Гарантия
  const warranty = product.warranty || warrantyByFrame['20x20'];

  // Формируем список выбранных опций
  const getSelectedOptions = () => {
    const options = [];
    
    options.push({
      name: `Поликарбонат ${currentPolycarbonate.name} ${currentPolycarbonate.density} (${currentPolycarbonate.type})`,
      price: polycarbonatePrice
    });

    if (fortochkaCount > 0) {
      options.push({
        name: `Дополнительная форточка x${fortochkaCount}`,
        price: fortochkaPrice
      });
    }

    if (termoprivodCount > 0) {
      options.push({
        name: `Термопривод x${termoprivodCount}`,
        price: termoprivodPrice
      });
    }

    if (termoshayby) {
      options.push({
        name: 'Термошайбы увеличенные',
        price: termoshaybyPrice
      });
    }

    if (fundament) {
      options.push({
        name: 'Фундамент из бруса',
        price: fundamentPrice
      });
    }

    if (montazh) {
      options.push({
        name: 'Монтаж',
        price: montazhPrice
      });
    }

    if (gryadki) {
      options.push({
        name: 'Грядки оцинкованные',
        price: gryadkiPrice
      });
    }

    return options;
  };

  const handleAddToCart = () => {
    const selectedOptions = getSelectedOptions();
    const productWithOptions = {
      ...product,
      price: totalPrice,
      options: {
        polycarbonate: `${currentPolycarbonate.name} ${currentPolycarbonate.density} (${currentPolycarbonate.type})`,
        additionalOptions: selectedOptions
      },
      selectedOptions: selectedOptions
    };
    addToCart(productWithOptions, quantity);
  };

  const handleOrderNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  return (
    <Layout>
      {/* SEO Helmet with Schema.org Product */}
      <Helmet>
        <title>{product.name} — купить в Башкортостане | Народные теплицы</title>
        <meta name="description" content={`${product.name} от производителя. Цена ${formatPrice(product.price)} ₽. ${product.frame}, гарантия ${warranty} лет. Доставка по Башкортостану.`} />
        <meta name="keywords" content={`${product.name}, купить теплицу, теплица из поликарбоната, ${product.category}`} />
        <link rel="canonical" href={`https://теплицынадом.рф/product/${product.slug}`} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.images[0],
            "sku": product.id,
            "brand": {
              "@type": "Brand",
              "name": "Народные теплицы"
            },
            "offers": {
              "@type": "Offer",
              "url": `https://теплицынадом.рф/product/${product.slug}`,
              "priceCurrency": "RUB",
              "price": product.price,
              "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
              "seller": {
                "@type": "Organization",
                "name": "Народные теплицы"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "47"
            }
          })}
        </script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-emerald-600">Главная</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/catalog" className="hover:text-emerald-600">Каталог</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Info */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Images */}
            <div>
              <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-4">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? 'border-emerald-500' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <p className="text-sm text-gray-500 mt-4 text-center">
                * Фотографии могут отличаться от реального товара
              </p>
            </div>

            {/* Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {product.isHit && <Badge className="bg-amber-500 hover:bg-amber-500">Хит продаж</Badge>}
                {product.isNew && <Badge className="bg-emerald-600 hover:bg-emerald-600">Новинка</Badge>}
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>

              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-gray-500">Отзывы клиентов</span>
              </div>

              <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

              {/* Выбор поликарбоната */}
              <div className="bg-emerald-50 rounded-xl p-5 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm">1</span>
                    Выберите поликарбонат
                  </h3>
                  <button
                    onClick={() => setShowPolyInfo(true)}
                    className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 text-sm"
                  >
                    <Info className="w-4 h-4" />
                    Чем отличается? *
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {polycarbonateOptions.map((poly) => {
                    // Цена для данного размера: для Домиком - priceBySizeDomikom, для остальных - priceBySize
                    const polyPrice = isDomikom 
                      ? (poly.priceBySizeDomikom?.[sizeKey] || poly.priceAdd)
                      : (poly.priceBySize?.[sizeKey] || poly.priceAdd);
                    
                    return (
                      <button
                        key={poly.id}
                        onClick={() => setSelectedPolycarbonate(poly.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          selectedPolycarbonate === poly.id
                            ? 'border-emerald-500 bg-emerald-100'
                            : 'border-gray-200 bg-white hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-gray-900">{poly.name}</div>
                            <div className="text-sm text-gray-500">Плотность: {poly.density}</div>
                          </div>
                          <Badge className={`text-xs ${
                            poly.type === 'Эконом' ? 'bg-gray-500' :
                            poly.type === 'Стандарт' ? 'bg-blue-500' :
                            poly.type === 'Стандарт+' ? 'bg-emerald-500' :
                            'bg-amber-500'
                          }`}>
                            {poly.type}
                          </Badge>
                        </div>
                        {polyPrice > 0 && (
                          <div className="text-sm text-emerald-600 mt-2">+{formatPrice(polyPrice)} ₽</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Дополнительные опции */}
              <div className="bg-blue-50 rounded-xl p-5 mb-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Дополнительные опции
                </h3>
                
                {/* Форточка */}
                <div className="flex items-center justify-between py-3 border-b border-blue-100">
                  <div>
                    <div className="font-medium">Дополнительная форточка</div>
                    <div className="text-sm text-gray-500">{formatPrice(additionalOptions.fortochka.price)} ₽/шт</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setFortochkaCount(Math.max(0, fortochkaCount - 1))}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-semibold">{fortochkaCount}</span>
                    <button
                      onClick={() => setFortochkaCount(fortochkaCount + 1)}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Термопривод */}
                <div className="flex items-center justify-between py-3 border-b border-blue-100">
                  <div>
                    <div className="font-medium">Термопривод</div>
                    <div className="text-sm text-gray-500">{formatPrice(additionalOptions.termoprivod.price)} ₽/шт</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setTermoprivodCount(Math.max(0, termoprivodCount - 1))}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-semibold">{termoprivodCount}</span>
                    <button
                      onClick={() => setTermoprivodCount(termoprivodCount + 1)}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Термошайбы */}
                <div className="flex items-center justify-between py-3 border-b border-blue-100">
                  <div>
                    <div className="font-medium">Термошайбы увеличенные</div>
                    <div className="text-sm text-gray-500">{formatPrice(additionalOptions.termoshayby.priceBySize[sizeKey] || 0)} ₽</div>
                  </div>
                  <button
                    onClick={() => setTermoshayby(!termoshayby)}
                    className={`w-12 h-6 rounded-full transition-colors ${termoshayby ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${termoshayby ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Фундамент */}
                <div className="flex items-center justify-between py-3 border-b border-blue-100">
                  <div>
                    <div className="font-medium">Фундамент из бруса</div>
                    <div className="text-sm text-gray-500">{formatPrice(additionalOptions.fundament.priceBySize[sizeKey] || 0)} ₽</div>
                  </div>
                  <button
                    onClick={() => setFundament(!fundament)}
                    className={`w-12 h-6 rounded-full transition-colors ${fundament ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${fundament ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Монтаж */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">Монтаж</div>
                    <div className="text-sm text-gray-500">{formatPrice(
                      isDomikom 
                        ? (additionalOptions.montazh.priceBySizeDomikom?.[sizeKey] || additionalOptions.montazh.priceBySize[sizeKey] || 0)
                        : (additionalOptions.montazh.priceBySize[sizeKey] || 0)
                    )} ₽</div>
                  </div>
                  <button
                    onClick={() => setMontazh(!montazh)}
                    className={`w-12 h-6 rounded-full transition-colors ${montazh ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${montazh ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Грядки оцинкованные */}
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <div className="font-medium">Грядки оцинкованные</div>
                    <div className="text-sm text-gray-500">{formatPrice(additionalOptions.gryadki.priceBySize[sizeKey] || 0)} ₽</div>
                  </div>
                  <button
                    onClick={() => setGryadki(!gryadki)}
                    className={`w-12 h-6 rounded-full transition-colors ${gryadki ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${gryadki ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Характеристики */}
              <div className="bg-gray-50 rounded-xl p-5 mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Характеристики</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <dt className="text-gray-500">Размер (ШxДxВ)</dt>
                  <dd className="font-medium">{product.size.width}x{product.size.length}x{product.size.height} м</dd>
                  <dt className="text-gray-500">Каркас</dt>
                  <dd className="font-medium">{product.frame}</dd>
                  <dt className="text-gray-500">Шаг между дугами</dt>
                  <dd className="font-medium">{product.step || '65 см'}</dd>
                  <dt className="text-gray-500">Гарантия</dt>
                  <dd className="font-medium">{warranty} {warranty === 1 ? 'год' : warranty < 5 ? 'года' : 'лет'}</dd>
                </dl>
              </div>

              {/* Стандартная комплектация */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Стандартная комплектация</h3>
                <ul className="space-y-2">
                  {standardEquipment.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-600">
                      <Check className="w-5 h-5 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Список выбранных опций */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-amber-800 mb-3">Ваш выбор:</h4>
                <ul className="text-sm space-y-2">
                  <li className="flex justify-between">
                    <span>{product.name}</span>
                    <span className="font-medium">{formatPrice(product.price)} ₽</span>
                  </li>
                  {getSelectedOptions().map((option, idx) => (
                    <li key={idx} className="flex justify-between text-amber-700">
                      <span>+ {option.name}</span>
                      <span>{option.price > 0 ? `+${formatPrice(option.price)} ₽` : 'Включено'}</span>
                    </li>
                  ))}
                  <li className="flex justify-between font-bold text-lg pt-3 border-t border-amber-300 mt-3 text-amber-900">
                    <span>Итого:</span>
                    <span>{formatPrice(totalPrice)} ₽</span>
                  </li>
                </ul>
              </div>

              {/* Количество */}
              <div className="flex items-center gap-4 mb-4">
                <span className="text-gray-600">Количество:</span>
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Кнопки */}
              <div className="space-y-3 mb-6">
                <Button
                  size="lg"
                  onClick={handleAddToCart}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-14 text-lg"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Добавить в корзину
                </Button>
                <Button
                  size="lg"
                  onClick={handleOrderNow}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 h-14 text-lg"
                >
                  Оформить заказ
                </Button>
              </div>

              {/* Статус */}
              <p className={`text-sm font-medium mb-6 ${product.inStock ? 'text-emerald-600' : 'text-orange-500'}`}>
                {product.inStock ? '✓ В наличии' : 'Под заказ, 5-7 дней'}
              </p>

              {/* Быстрые контакты */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-3">Есть вопросы? Свяжитесь с нами:</p>
                <div className="flex flex-wrap gap-2">
                  <a href={`tel:${companyInfo.phoneClean}`}>
                    <Button variant="outline" size="sm">
                      <Phone className="w-4 h-4 mr-2" />
                      {companyInfo.phone}
                    </Button>
                  </a>
                  <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-green-500 text-green-600">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                  <a href={companyInfo.social.telegram} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-sky-500 text-sky-600">
                      <Send className="w-4 h-4 mr-2" />
                      Telegram
                    </Button>
                  </a>
                  <a href={companyInfo.social.max} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-purple-500 text-purple-600">
                      <span className="font-bold mr-2">M</span>
                      Max
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Модальное окно с информацией о поликарбонате */}
      {showPolyInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">{polycarbonateInfo.title}</h3>
                <button
                  onClick={() => setShowPolyInfo(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {polycarbonateInfo.content.map((item, idx) => (
                  <div key={idx} className="border-b border-gray-100 pb-3 last:border-0">
                    <h4 className="font-semibold text-emerald-700">{item.label}</h4>
                    <p className="text-gray-600 text-sm mt-1">{item.text}</p>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setShowPolyInfo(false)}
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
              >
                Понятно
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="reviews" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="reviews">Отзывы</TabsTrigger>
              <TabsTrigger value="specs">Характеристики</TabsTrigger>
            </TabsList>
            <TabsContent value="specs" className="bg-white rounded-xl p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Ширина</dt>
                  <dd className="font-medium">{product.size.width} м</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Длина</dt>
                  <dd className="font-medium">{product.size.length} м</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Высота</dt>
                  <dd className="font-medium">{product.size.height} м</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Каркас</dt>
                  <dd className="font-medium">{product.frame}</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Шаг дуг</dt>
                  <dd className="font-medium">{product.step || '65 см'}</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Гарантия</dt>
                  <dd className="font-medium">{warranty} {warranty === 1 ? 'год' : warranty < 5 ? 'года' : 'лет'}</dd>
                </div>
              </dl>
              <p className="text-sm text-gray-500 mt-6">{warrantyDisclaimer}</p>
            </TabsContent>
            <TabsContent value="reviews" className="bg-white rounded-xl p-6">
              <div className="space-y-6">
                {reviews.slice(0, 4).map(review => (
                  <div key={review.id} className="border-b pb-6 last:border-0">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-3">{review.text}</p>
                    <p className="text-sm text-gray-500">{review.name}, {review.location}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Похожие товары</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ProductPage;
